import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { getRecentTicketIds } from '../../lib/recents';

/** Types + hooks for the global search feature (mirrors modules/search DTOs). */

export interface SearchHighlight {
  field: string;
  snippet: string;
}

export interface SearchResult {
  id: string;
  entityType: string;
  entityId: string;
  parentEntityType: string | null;
  parentEntityId: string | null;
  title: string;
  key: string | null;
  url: string;
  score: number;
  status: string | null;
  priority: string | null;
  ticketType: string | null;
  updatedAt: string;
  matchedFields: string[];
  highlights: SearchHighlight[];
  metadata: Record<string, unknown> & {
    assignees?: { id: string; name: string }[];
    tags?: { id: string; name: string; slug: string; color: string }[];
    customer?: string | null;
    commentMatchCount?: number;
    attachmentMatchCount?: number;
    archived?: boolean;
    authorName?: string | null;
    ticketCode?: string;
    ticketTitle?: string;
    email?: string;
  };
}

export interface QuickSearchData {
  query: string;
  mode: 'empty' | 'ticket_key' | 'aql' | 'keyword';
  bestResults: SearchResult[];
  tickets: SearchResult[];
  docs: SearchResult[];
  comments: SearchResult[];
  entities: SearchResult[];
  totalEstimated: number;
  didYouMean: string | null;
  tookMs: number;
}

export interface SearchFacet { value: string; count: number }

export interface FullSearchData {
  query: string;
  mode: string;
  results: SearchResult[];
  pagination: { page: number; pageSize: number; total: number };
  facets: {
    entityType: SearchFacet[];
    status: SearchFacet[];
    priority: SearchFacet[];
    ticketType: SearchFacet[];
  } | null;
  didYouMean: string | null;
  tookMs: number;
  historyId?: string | null;
}

export interface SearchHistoryItem {
  id: string;
  queryText: string;
  source: string;
  resultCount: number;
  createdAt: string;
}

export interface SavedSearch {
  id: string;
  ownerId: string;
  name: string;
  description: string | null;
  queryText: string;
  filters: Record<string, unknown>;
  sortKey: string | null;
  entityTypes: string[];
  visibility: 'private' | 'shared';
  createdAt: string;
  updatedAt: string;
}

export function useQuickSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['search', 'quick', q],
    queryFn: () =>
      api.get<QuickSearchData>(
        `/v2/search/quick${qs({ q, recent: getRecentTicketIds().join(',') })}`,
      ),
    enabled: enabled && q.trim().length >= 2,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useSearchHistory(enabled: boolean) {
  return useQuery({
    queryKey: ['search', 'history'],
    queryFn: () => api.get<{ items: SearchHistoryItem[] }>('/v2/search/history'),
    enabled,
    staleTime: 30_000,
  });
}

export function useSavedSearches(enabled = true) {
  return useQuery({
    queryKey: ['search', 'saved'],
    queryFn: () => api.get<{ items: SavedSearch[] }>('/v2/search/saved'),
    enabled,
    staleTime: 30_000,
  });
}

/** Fire-and-forget click tracking (never blocks navigation). */
export function trackSearchClick(queryText: string, r: SearchResult, historyId?: string | null) {
  void api
    .post('/v2/search/history', {
      queryText,
      click: { historyId: historyId ?? undefined, entityType: r.entityType, entityId: r.entityId },
    })
    .catch(() => undefined);
}
